import ReactGA from 'react-ga4';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { axiosDefault } from '@/core/utils';
////////////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { BackButton, PageTitle, SettingOption } from '@/app/shared/components';
import { Option } from '@/core/interfaces';
import { getLanguagesData } from '@/core/utils';
import { setUpdatedLang } from '@/core/redux/slices';
import { useCustomDispatch } from '@/core/redux/hooks';

/**
 * ChooseLanguage component allows users to select a language from a list of available languages.
 *
 * Hooks:
 * - useTranslation: Provides translation functions for internationalization support.
 * - useCustomDispatch: Provides the dispatch function for Redux actions.
 * - useEffect: Sends a pageview event to Google Analytics when the component mounts.
 *
 * State:
 * - languageSelected: Tracks the currently selected language.
 *
 * Functions:
 * - handleOnSelectedLanguageChange: Handles language selection changes, updates the language,
 *   and dispatches the updated language to the Redux store. Displays toast notifications for success and error states.
 *
 * Template:
 * - Renders a BackButton and PageTitle component.
 * - Maps over the languagesData array to render SettingOption components for each language.
 * - The selected language is highlighted, and selecting a different language triggers the handleOnSelectedLanguageChange function.
 */
const ChooseLanguage: React.FC = () => {
	/*********************
	// #region Constants
	**********************/

	/*********************
	 // #region Hooks
	 **********************/
	const languagesData = getLanguagesData();
	const { i18n, t } = useTranslation();
	const dispatch = useCustomDispatch();

	/*********************
	// #region States
	**********************/
	const [languageSelected, setLanguageSelected] = useState<string>(
		i18n.language
	);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/settings/language',
			title: 'Choose Language',
		});
	}, []);

	/*********************
	// #region Functions  
	**********************/
	const handleOnSelectedLanguageChange = async (lang: Option) => {
		if (!lang.code) return;

		if (
			telegramWebApp.platform === 'android' ||
			telegramWebApp.platform === 'ios' ||
			telegramWebApp.platform === 'android_x' ||
			process.env.VITE_ENV_APP === 'dev' ||
			process.env.VITE_ENV_APP === 'int'
		) {
			// Local
			localStorage.setItem('lang_selected', lang.code);
			setLanguageSelected(lang.code);
			i18n.changeLanguage(lang.code);
			toast.success(
				`${t('settings.language.updateMessage')} ${lang.name}`
			);
		} else {
			// Production
			telegramWebApp.HapticFeedback.impactOccurred('heavy');
			telegramWebApp.CloudStorage.setItem(
				'lang_selected',
				lang.code,
				(err, status) => {
					if (err) {
						toast.error(err);
					}
					if (status) {
						setLanguageSelected(lang.code!);
						i18n.changeLanguage(lang.code);
						toast.success(
							`${t('settings.language.updateMessage')} ${
								lang.name
							}`
						);
					}
				}
			);
		}
		axiosDefault.defaults.headers.common['x-language'] = lang.code;
		dispatch(setUpdatedLang(lang.code));
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pt-8 pb-32 mx-4">
			<BackButton />
			<div className="flex flex-col items-center">
				<PageTitle>{t('settings.language.title')}</PageTitle>
			</div>
			<div className="flex flex-col w-full gap-3">
				{languagesData.map((element) => {
					return (
						<SettingOption
							key={element.id}
							option={element}
							isChecked={languageSelected == element.code}
							onSelectOption={(option) => {
								handleOnSelectedLanguageChange(option);
							}}
						/>
					);
				})}
			</div>
		</div>
	);
};

export default ChooseLanguage;
